import { useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../app/store";
import { clearCaseCreationStatus } from "../../cases/logic/CaseCreationSlice";
import { createCase } from "../../cases/logic/Thunks";
import { formatDateForInput, getCurrentDate } from "../../../lib/utils";
import { AreaOfFraud } from "../../../api/cases";
import { AccountType as DepositAccountType, AccountCategory } from "../../../api/accounts";
import { isCreditAccountStakeholdersRevampEnabled, isOperaionalCyberRevampEnabled } from "../../configs/logic/Slice";
import {
    CreateCaseFormInterface,
    CreateCaseFormPayload,
    BasicDetailsState,
    BackgroundState,
    PartyInvolvedState,
    getCreateCaseRequest,
} from "../utils/helper";
import { AccountPartyState } from "./AccountPartyInvolvedStep";
import {
    CreditPartyState,
} from "./party-involved/derive";

import StepSelectType from "./StepSelectType";
import CaseBasicDetailsStep from "./StepBasicDetails";
import CaseBackgroundStep from "./StepBackground";
import CreditAccountPartyInvolvedStep from "./CreditAccountPartyInvolvedStep";
import AccountPartyInvolvedStep from "./AccountPartyInvolvedStep";
import CaseReviewStep from "./StepReview";
import CaseCreateSuccess from "./CaseCreateSuccess";
import Spinner from "../../../components/ui/spinners/CompactLoadingSpinner";
import PartyInvolvedStepLegacy from "./PartyInvolvedStepLegacy";

const initialBasicDetails = (): BasicDetailsState => ({
    source: undefined,
    referrer_name: "",
    referral_date: formatDateForInput(getCurrentDate()),
    region: undefined,
    zone: undefined,
    branch: undefined,
    forwarding_authority: undefined,
});
const initialBackground = (): BackgroundState => ({ investigation_nature: undefined, background: "" });
const initialPartyInvolved = (): PartyInvolvedState => ({
    area_of_fraud: undefined,
    account_type: undefined,
    has_employee_involvement: false,
    employee_ids: undefined,
    parties_involved: [],
    loanDetails: [],
});
const initialCreditParty = (): CreditPartyState => ({
    loanAccounts: [],
    primaryParties: [],
});
const initialAccountParty = (): AccountPartyState => ({
    accounts: [],
    primaryParties: [],
});

const CreateCaseForm: React.FC<CreateCaseFormInterface> = ({ currentStep = 0, onStepChange }) => {
    const dispatch = useAppDispatch();
    const creditRevampEnabled = useAppSelector(isCreditAccountStakeholdersRevampEnabled);
    const operationalCyberRevampEnabled = useAppSelector(isOperaionalCyberRevampEnabled);

    const [basicDetails, setBasicDetails] = useState<BasicDetailsState>(initialBasicDetails);
    const [background, setBackground] = useState<BackgroundState>(initialBackground);
    const [partyInvolved, setPartyInvolved] = useState<PartyInvolvedState>(initialPartyInvolved);
    const [creditParty, setCreditParty] = useState<CreditPartyState>(initialCreditParty);
    const [accountParty, setAccountParty] = useState<AccountPartyState>(initialAccountParty);

    const [isCreating, setIsCreating] = useState(false);
    const [isFailed, setIsFailed] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const updateBasicDetails = (field: string, value: any) => setBasicDetails((prev) => ({ ...prev, [field]: value }));
    const updateBackground = (field: string, value: any) => setBackground((prev) => ({ ...prev, [field]: value }));
    const updatePartyInvolved = (field: string, value: any) => setPartyInvolved((prev) => ({ ...prev, [field]: value }));
    const updateCreditParty = (patch: Partial<CreditPartyState>) => setCreditParty((prev) => ({ ...prev, ...patch }));
    const updateAccountParty = (patch: Partial<AccountPartyState>) => setAccountParty((prev) => ({ ...prev, ...patch }));

    const payload = useMemo<CreateCaseFormPayload>(
        () => ({ ...basicDetails, ...background, ...partyInvolved }),
        [basicDetails, background, partyInvolved]
    );

    const areaOfFraud = payload.area_of_fraud?.value as AreaOfFraud | undefined;
    const isCredit = areaOfFraud === AreaOfFraud.CREDIT;
    const showCreditRevampParty = isCredit && creditRevampEnabled;
    const showLegacyPartyInvolved = (isCredit && !creditRevampEnabled) || (!isCredit && !operationalCyberRevampEnabled);
    const showAccountPartyInvolved = !isCredit && operationalCyberRevampEnabled;

    const goNext = () => onStepChange?.(currentStep + 1);
    const goPrevious = () => onStepChange?.(currentStep - 1);

    const handleAreaOfFraudChange = (next: AreaOfFraud) => {
        const cardTitle =
            next === AreaOfFraud.CREDIT
                ? "Credit Fraud"
                : next === AreaOfFraud.CYBER
                  ? "Cyber Fraud"
                  : "Operational Fraud";
        updatePartyInvolved("area_of_fraud", {
            name: cardTitle,
            value: next,
        });
        // Clear the other draft so switching type does not leak state.
        if (next === AreaOfFraud.CREDIT) {
            setAccountParty(initialAccountParty());
        } else {
            setCreditParty(initialCreditParty());
        }
    };

    const resetForm = () => {
        setBasicDetails(initialBasicDetails());
        setBackground(initialBackground());
        setPartyInvolved(initialPartyInvolved());
        setCreditParty(initialCreditParty());
        setAccountParty(initialAccountParty());
        setIsCreating(false);
        setIsFailed(false);
        setIsSuccess(false);
        onStepChange?.(0);
        dispatch(clearCaseCreationStatus());
    };

    const handleSubmit = async () => {
        setIsCreating(true);
        setIsFailed(false);
        let request;
        if (showCreditRevampParty) {
            request = getCreateCaseRequest(payload, {
                loanAccounts: creditParty.loanAccounts,
                primaryParties: creditParty.primaryParties,
            });
        } else if (showAccountPartyInvolved) {
            request = getCreateCaseRequest(payload, undefined, {
                accounts: accountParty.accounts
                    .filter(
                        (a) =>
                            a.account_type &&
                            a.account_category &&
                            a.ext_account_number.trim() &&
                            a.account_name.trim(),
                    )
                    .map((a) => ({
                        account_number: a.account_number,
                        ext_account_number: a.ext_account_number.trim(),
                        account_name: a.account_name.trim(),
                        account_type: a.account_type as DepositAccountType,
                        account_category: a.account_category as AccountCategory,
                    })),
                primaryParties: accountParty.primaryParties,
            });
        } else {
            request = getCreateCaseRequest(payload);
        }
        const result = await dispatch(createCase(request));
        setIsCreating(false);
        if (result.meta.requestStatus === "fulfilled") setIsSuccess(true);
        else setIsFailed(true);
    };

    return (
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto">
            {isCreating && <Spinner isFixed message="Creating Case..." />}
            {isFailed &&
                <div className="mb-8">
                    <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-md p-4">
                        Failed to create case. Please try again.
                    </div>
                </div>
            }
            {isSuccess ?
                <CaseCreateSuccess reset={resetForm} />
                :
                <div className="card bg-white shadow-formCard rounded-lg p-6 mt-6 mb-8">
                    {currentStep === 0 &&
                        <StepSelectType
                            areaOfFraud={areaOfFraud}
                            onAreaOfFraudChange={handleAreaOfFraudChange}
                            onNext={goNext}
                        />
                    }
                    {currentStep === 1 &&
                        <CaseBasicDetailsStep value={basicDetails} onChange={updateBasicDetails} onNext={goNext} onPrevious={goPrevious} />
                    }
                    {currentStep === 2 &&
                        <CaseBackgroundStep value={background} onChange={updateBackground} onNext={goNext} onPrevious={goPrevious} />
                    }
                    {currentStep === 3 &&
                        <>
                            {showLegacyPartyInvolved &&
                                <PartyInvolvedStepLegacy
                                    value={partyInvolved}
                                    handleFormInputChange={updatePartyInvolved}
                                    onNext={goNext}
                                    onPrevious={goPrevious}
                                />
                            }
                            {showCreditRevampParty &&
                                <CreditAccountPartyInvolvedStep
                                    areaOfFraud={partyInvolved.area_of_fraud}
                                    onNext={goNext}
                                    onPrevious={goPrevious}
                                    creditParty={creditParty}
                                    updateCreditParty={updateCreditParty}
                                />
                            }
                            {showAccountPartyInvolved &&
                                <AccountPartyInvolvedStep
                                    areaOfFraud={areaOfFraud!}
                                    accountParty={accountParty}
                                    updateAccountParty={updateAccountParty}
                                    onNext={goNext}
                                    onPrevious={goPrevious}
                                />
                            }
                        </>
                    }
                    {currentStep === 4 &&
                        <CaseReviewStep
                            payload={payload}
                            onSubmit={handleSubmit}
                            onPrevious={goPrevious}
                            isBusy={isCreating || isSuccess}
                            showLegacyPartyInvolved={showLegacyPartyInvolved}
                            loanAccounts={creditParty.loanAccounts}
                            parties={creditParty.primaryParties}
                            accountParties={accountParty.primaryParties}
                            accounts={accountParty.accounts}
                            showAccountPartyInvolved={showAccountPartyInvolved}
                        />
                    }
                </div>
            }
        </div>
    );
};

export default CreateCaseForm;
